import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { CONTENT_TYPE_TOKEN } from './table-content.service';

export const LOCKED_START_COLUMN: string = 'title';
export const LOCKED_END_COLUMN: string = 'actions';

@Injectable()
export class TableColumnsService {
  private readonly storageKey: string = `${inject(CONTENT_TYPE_TOKEN)}_hidden_columns`;

  readonly columns: WritableSignal<string[]> = signal<string[]>([]);
  readonly hiddenColumns: WritableSignal<string[]> = signal<string[]>(this.loadHiddenColumns());

  readonly visibleColumns: Signal<string[]> = computed(() => [
    LOCKED_START_COLUMN,
    ...this.columns().filter((column: string) => !this.hiddenColumns().includes(column)),
    LOCKED_END_COLUMN,
  ]);

  setColumns(columns: string[]): void {
    this.columns.set(columns);
  }

  canHideColumn(column: string): boolean {
    return this.columns().includes(column);
  }

  isHidden(column: string): boolean {
    return this.hiddenColumns().includes(column);
  }

  setColumnVisibility(column: string, visible: boolean): void {
    if (!this.canHideColumn(column) || this.isHidden(column) === !visible) return;

    this.saveHiddenColumns(
      visible
        ? this.hiddenColumns().filter((hidden: string) => hidden !== column)
        : [...this.hiddenColumns(), column],
    );
  }

  hideColumn(column: string): void {
    this.setColumnVisibility(column, false);
  }

  resetHiddenColumns(): void {
    this.saveHiddenColumns([]);
  }

  reorderColumns(displayedColumns: string[]): void {
    const visibleOrder: string[] = displayedColumns.filter((column: string) =>
      this.canHideColumn(column),
    );
    let visibleIndex: number = 0;

    this.columns.update((columns: string[]) =>
      columns.map((column: string) =>
        this.isHidden(column) ? column : (visibleOrder[visibleIndex++] ?? column),
      ),
    );
  }

  private saveHiddenColumns(hiddenColumns: string[]): void {
    this.hiddenColumns.set(hiddenColumns);
    localStorage.setItem(this.storageKey, JSON.stringify(hiddenColumns));
  }

  private loadHiddenColumns(): string[] {
    try {
      const hiddenColumns: unknown = JSON.parse(localStorage.getItem(this.storageKey) ?? '[]');
      return Array.isArray(hiddenColumns) ? hiddenColumns : [];
    } catch {
      return [];
    }
  }
}
